import React, { useEffect, useState } from "react";

import { Button, Icon, Modal, toast, Text } from "@telia/teddy";
import { useDispatch } from "react-redux";
import { ICartItemWithBundleId } from "../../../../../../models";
import { getFormattedPrice } from "../../../../../../shared/priceFormatter";
import { setNegativeFlash } from "../../../../app/state/generalActions";
import { trackWebshopClickEvent } from "../../../../google-analytics/googleAnalyticsEventHelper";
import { applyFlexibleLease } from "../../../state/actions/cartActions";
import RadioButtonCard from "../RadioButtonCard/RadioButtonCard";
import "./LeaseLimitModal.less";

type Option = "allUpfront" | "partlyUpfront";

interface Props {
    cartItem?: ICartItemWithBundleId;
    close: (shouldResubmit: boolean) => void;
    isCreditCheckUnavailable?: boolean;
    maxAllowedLeaseAmount: number;
    onRetryCreditCheck?: () => void;
    open: boolean;
    urlForTracking: string;
}

const MODAL_TEXTS = {
    supportsPartlyUpfront: {
        title: "Delbetaling av hele kjøpesummen er ikke tilgjengelig",
        description:
            "Vi kan dessverre ikke tilby deg full delbetaling. Du kan fortsatt gjennomføre kjøpet og få samme rabatt, men må betale hele eller en del av beløpet i dag. Hvilket alternativ ønsker du?",
        allUpfrontDescription: "Jeg vil betale hele beløpet nå, og ingenting på delbetaling.",
    },
    creditCheckUnavailable: {
        title: "Kredittsjekken er midlertidig utilgjengelig",
        description:
            "Vi klarte ikke å gjennomføre kredittsjekken akkurat nå, og kan derfor ikke tilby delbetaling. Du kan prøve kredittsjekken på nytt, eller gjennomføre kjøpet og betale hele beløpet i dag.",
        allUpfrontDescription: "Jeg vil betale hele beløpet nå",
    },
    onlyAllUpfront: {
        title: "Delbetaling er ikke tilgjengelig",
        description:
            "Vi kan dessverre ikke tilby deg delbetaling. Du kan fortsatt gjennomføre kjøpet og få samme rabatt, men må betale hele beløpet i dag.",
        allUpfrontDescription: "Jeg vil betale hele beløpet nå",
    },
};

const LeaseLimitModal: React.FC<Props> = ({
    cartItem,
    maxAllowedLeaseAmount,
    close,
    open,
    urlForTracking,
    isCreditCheckUnavailable,
    onRetryCreditCheck,
}) => {
    const [selectedOption, setSelectedOption] = useState<Option>("allUpfront");
    const dispatch = useDispatch();

    useEffect(() => {
        if (!cartItem || cartItem.leaseMonths === undefined || cartItem.price === undefined) {
            // oxlint-disable-next-line no-console
            console.error("Error: Displayed leaseLimitModal when it should not", { cartItem });
            dispatch(
                setNegativeFlash(
                    "Beklager, noe har gått galt. Feilen har blitt rapportert inn. Vennligst prøv igjen senere.",
                ),
            );
        }
    }, []);

    if (!cartItem || cartItem.leaseMonths === undefined || cartItem.price === undefined) {
        return;
    }

    const leaseMonths = cartItem.leaseMonths.toString();
    const price = cartItem.price.total!;
    const safeMaxAllowedLeaseAmount =
        Number.isFinite(maxAllowedLeaseAmount) && maxAllowedLeaseAmount >= 0
            ? Math.min(maxAllowedLeaseAmount, price)
            : 0;
    const leasePart = Math.floor(safeMaxAllowedLeaseAmount / parseInt(leaseMonths));

    const handleOnNext = async () => {
        await dispatch(applyFlexibleLease(cartItem.bundleId, selectedOption, safeMaxAllowedLeaseAmount));

        trackWebshopClickEvent({
            action: "click",
            text: "Neste",
            type: "button",
            url: urlForTracking,
            context: selectedOption === "allUpfront" ? "flexible_lease_allUpfront" : "flexible_lease_partlyUpfront",
        });

        toast.success(`Handlekurven har blitt oppdatert`, {
            position: "top-right",
        });

        close(true);
    };

    const supportsPartlyUpfront = safeMaxAllowedLeaseAmount > 0;
    const getModalTexts = () => {
        if (isCreditCheckUnavailable) {
            return MODAL_TEXTS.creditCheckUnavailable;
        }
        return supportsPartlyUpfront ? MODAL_TEXTS.supportsPartlyUpfront : MODAL_TEXTS.onlyAllUpfront;
    };
    const modalTexts = getModalTexts();

    return (
        <Modal defaultOpen open={open}>
            <Modal.Overlay className="lease-limit-modal__container">
                <Modal.Content className="lease-limit-modal__content">
                    <Modal.Group position="relative">
                        <Modal.Title as="h3" position="relative">
                            {modalTexts.title}
                        </Modal.Title>
                    </Modal.Group>
                    <Modal.Close slot="floating" aria-label="lukk" onClick={() => close(false)} />
                    <Modal.Description>{modalTexts.description}</Modal.Description>
                    {supportsPartlyUpfront && (
                        <RadioButtonCard
                            selectedValue={selectedOption}
                            setSelectedValue={(value) => setSelectedOption(value as Option)}
                            options={[
                                {
                                    value: "allUpfront",
                                    heading: "Betale alt nå",
                                    description: (
                                        <div>
                                            <div className="lease-limit-modal__radio-button-card-description">
                                                <Text mb="100">{modalTexts.allUpfrontDescription}</Text>
                                            </div>
                                            <div className="lease-limit-modal__price">
                                                Å betale nå: {getFormattedPrice(price)}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    value: "partlyUpfront",
                                    heading: "Betal deler av beløpet nå",
                                    description: (
                                        <div>
                                            <div className="lease-limit-modal__radio-button-card-description">
                                                <Text mb="100">
                                                    Jeg vil betale{" "}
                                                    <span className="lease-limit-modal__radio-button-card-description--bold">
                                                        {getFormattedPrice(leasePart)}
                                                        /md i {leaseMonths} md.
                                                    </span>{" "}
                                                    med rentefri delbetaling og resten av beløpet nå.
                                                </Text>
                                            </div>
                                            <div className="lease-limit-modal__price">
                                                Å betale nå:{" "}
                                                {getFormattedPrice(price - leasePart * parseInt(leaseMonths))}
                                            </div>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    )}
                    {!supportsPartlyUpfront && (
                        <div className="lease-limit-modal__standalone-price">
                            Å betale nå: {getFormattedPrice(price - leasePart * parseInt(leaseMonths))}
                        </div>
                    )}
                    <Modal.Group variant="action" position="relative">
                        <Button
                            className="lease-limit-modal__back-button"
                            variant="text"
                            asChild
                            onClick={() => {
                                trackWebshopClickEvent({
                                    action: "click",
                                    text: "Neste",
                                    type: "button",
                                    url: "/kasse/medlem",
                                    context: "flexible_lease_backToPDP",
                                });
                            }}
                        >
                            <a href={cartItem.href || "javascript:void(0)"}>
                                <Icon name="arrow-left" aria-hidden="true" focusable="false" />
                                Gå tilbake og endre produkt
                            </a>
                        </Button>

                        {isCreditCheckUnavailable && onRetryCreditCheck && (
                            <Button variant="secondary" size="md" onClick={() => onRetryCreditCheck()}>
                                Prøv kredittsjekk på nytt
                            </Button>
                        )}

                        <Button variant="primary" size="md" onClick={async () => await handleOnNext()}>
                            Neste
                        </Button>
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default LeaseLimitModal;
